/**
 * OvergrownConservatory -- Level 2.
 *
 * A Victorian glasshouse overtaken by botanical and mechanical growth.
 * Theme: Sequencing and Regulating.
 *
 * Per the 2026-04-14 companion book canon rewrite, the level runs on the
 * Incubation/Irrigation cycle rather than a day/night transition. Basil
 * flips the room at the Master Mist Regulator; Minna threads the room's
 * two distinct dangers (hunting foxes in Incubation, frozen opossums in
 * Irrigation that only walk when it's cool and misty).
 *
 * Puzzles (in order) -- each is a self-contained `PuzzleModule` under
 * `../levels/puzzles/overgrown-conservatory/`:
 *   1. Master Mist Regulator -- `MasterValvePuzzle`
 *   2. Nectar Escort         -- `NectarEscortPuzzle`
 *   3. Boiler-Bound Sundew   -- `BoilerBoundSundewPuzzle` (apex, 3-phase)
 *
 * Elder Gus encounter follows the Apex puzzle -> unlocks Chronos Bubble.
 *
 * This file is now the scene shell only: boot, camera, lighting,
 * post-processing, ConservatoryCycle system, environment art, ambient
 * particles, and the Elder encounter + level completion. All puzzle
 * geometry, NPCs, and win-condition logic lives in the puzzle modules.
 */
import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3, Color4 } from "@babylonjs/core/Maths/math.color";
import "@babylonjs/core/Lights/Shadows/shadowGeneratorSceneComponent";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import "@babylonjs/core/Rendering/depthRendererSceneComponent";
import {
  applyVisualProfile,
  applyPostProcessPreset,
  PostProcessPhaseTween,
  OVERGROWN_CONSERVATORY_PROFILE,
  UNDERGROUND_WARMTH,
  CONSERVATORY_IRRIGATION,
} from "../levels";
import { PBRMaterial } from "@babylonjs/core/Materials/PBR/pbrMaterial";
import "@babylonjs/loaders/glTF";

import {
  scatterInstances,
  type ScatterPlacement,
} from "../levels/staticOptimization";

import { PhysicsSystem } from "../systems/PhysicsSystem";
import { createConservatorySurfaces } from "../levels/art/ConservatorySurfaces";
import { loadConservatoryScenery } from "../levels/art/ConservatoryScenery";
import { ConservatoryAtmosphere } from "../levels/art/ConservatoryAtmosphere";
import { loadConservatoryRegulator } from "../levels/art/ConservatoryRegulator";
import { CameraSystem } from "../systems/CameraSystem";
import { InputManager } from "../systems/InputManager";
import { AbilityHolder } from "../systems/AbilitySystem";
import { AudioManager } from "../systems/AudioManager";
import { ConservatoryCycle } from "../systems/ConservatoryCycle";
import { CutsceneSystem, CutsceneStep } from "../systems/CutsceneSystem";
import { SaveSystem } from "../systems/SaveSystem";

import { InteractiveInfo } from "../abilities/minna/AuraSight";
import { ShadowZoneInfo } from "../abilities/minna/ShadowSlip";
import { ResonantDrop } from "../abilities/coop/ResonantDrop";
import { Portal } from "../entities/interactives/Portal";
import { GreaseCache } from "../entities/interactives/GreaseCache";
import { ChronosBubble } from "../abilities/coop/ChronosBubble";

import { gameEvents } from "../core/EventBus";
import { GameEvents } from "../config/events";
import { OVERGROWN_CONSERVATORY, WORLD_DEBUG } from "../config/GameConfig";
import { ELDER_GUS_ENCOUNTER, LEVEL_INTRO, controlHintForMode } from "../config/dialogue";

import { setupBaseLevel } from "./BaseLevel";
import { tryCoopAbility } from "./coopAbility";
import { setupElderBiscuitAnchor } from "./setupElderBiscuitAnchor";

import type { PuzzleContext, PuzzleModule, PuzzleState } from "../levels/PuzzleModule";
import { MasterValvePuzzle } from "../levels/puzzles/overgrown-conservatory/MasterValve";
import { NectarEscortPuzzle } from "../levels/puzzles/overgrown-conservatory/NectarEscort";
import { BoilerBoundSundewPuzzle } from "../levels/puzzles/overgrown-conservatory/BoilerBoundSundew";

import { ElderGus } from "../entities/npcs/ElderGus";
import { ConservatoryParlorVisuals } from "../levels/art/ConservatoryParlorVisuals";
import parlorPlacement from "../../assets/models-src/environment/conservatory_gus_parlor.json";
import { LevelCueDriver } from "../levels/LevelCueDriver";

export interface OvergrownConservatoryContext {
  physicsSystem: PhysicsSystem;
  cameraSystem: CameraSystem;
  inputManager: InputManager;
  minna: import("../entities/players/Minna").Minna;
  basil: import("../entities/players/Basil").Basil;
  minnaAbilities: AbilityHolder;
  basilAbilities: AbilityHolder;
  audioManager: AudioManager;
  conservatoryCycle: ConservatoryCycle;
  cutsceneSystem: CutsceneSystem;
}

// ─── Main Setup ─────────────────────────────────────────────────────

/**
 * Set up the Overgrown Conservatory scene.
 * Call after the engine and scene are created.
 */
export async function setupOvergrownConservatory(scene: Scene): Promise<OvergrownConservatoryContext> {
  // --- Base level setup ---
  const base = await setupBaseLevel(scene, {
    levelKey: "OvergrownConservatory",
    clearColor: Color4.FromHexString(OVERGROWN_CONSERVATORY.COLORS.BURROW_DEEP + "ff"),
    // Match OVERGROWN_CONSERVATORY_PROFILE.fog (profiles.ts) — the visual
    // profile applied just below is authoritative and overrides these a
    // few frames later, but the old BURROW_EARTH brown + 0.025 flashed a
    // muddy first frame before applyVisualProfile ran (Q-039).
    fogColor: new Color3(0.16, 0.33, 0.38),
    fogDensity: 0.006,
    minnaSpawn: new Vector3(5, 3, 0),
    basilSpawn: new Vector3(10, 3, 0),
    verticalFraming: { paddingAbove: 2.3, paddingBelow: 0.85, topInset: 0.18, bottomInset: 0.12 },
    auraSightVisualConfig: { glowIntensity: 0.32, threadRadius: 0.012, threadBrightness: 0.4 },
    calmingResonanceVisualConfig: {
      ringThickness: 0.02, ringOpacity: 0.4, ringFadeExponent: 2,
      ringPlane: "upright", ringHeightOffset: 0,
    },
    turnTableTouchConfig: { visualRadius: 0.85, visualDepthOffset: 0.8 },
    abilityVfxVisualConfig: { particleDensityScale: .25, particleSizeScale: .65 },
    resonanceStreamOpacity: 0.18,
  });

  const {
    physics: physicsSystem, minna, basil, minnaAbilities, basilAbilities,
    auraSight, shadowSlip, calmingResonance, turnTableTouch, pneumaticAnchor,
    camera: cameraSystem, audioManager, cutsceneSystem, inputManager,
  } = base;

  // --- Visual profile: lights, shadows, post-process, glow, fog, clearColor ---
  const visualHandles = applyVisualProfile(scene, OVERGROWN_CONSERVATORY_PROFILE, {
    glowLayer: base.getGlowLayer(),
  });
  base.addDisposeCallback(() => visualHandles.dispose());

  const glowLayer = visualHandles.glowLayer;
  const shadowGen = visualHandles.shadowGenerator!;
  base.shadowGenerator = shadowGen;

  // ConservatoryCycle retains phase timing, events and gameplay authority;
  // its internal day/midnight vocabulary maps to Irrigation/Incubation.
  const conservatoryCycle = new ConservatoryCycle(
    scene,
    visualHandles.hemiLight,
    visualHandles.keyLight,
  );

  // --- Dual-state post-process (Phase C1) ---
  // Canon L2 is a two-state room: Incubation (blown-out heat) and
  // Irrigation (cool mist). The visual profile seeds UNDERGROUND_WARMTH,
  // but ConservatoryCycle actually boots in `day` (= irrigation) — so
  // the starting post-process and the starting lighting were out of
  // phase, which Gemini 3.1-pro fluidity review caught as "teal lights
  // with warm grade." Sync the initial preset to the cycle's phase so
  // first-frame reads consistently, then keep the swap driven by
  // phase-changed events.
  const initialPostPreset = conservatoryCycle.isIrrigation
    ? CONSERVATORY_IRRIGATION
    : UNDERGROUND_WARMTH;
  applyPostProcessPreset(visualHandles.pipeline, initialPostPreset);
  const postTween = new PostProcessPhaseTween(visualHandles.pipeline, initialPostPreset);
  // Q-034: tween the grade in lockstep with the 1800ms light lerp instead
  // of snapping at lerp end. durationMs rides the event payload so the
  // grade always matches the cycle's actual lerp length.
  base.addUnsub(
    gameEvents.on(GameEvents.CONSERVATORY_CYCLE_TRANSITION, ({ to, durationMs }) => {
      postTween.start(
        to === "irrigation" ? CONSERVATORY_IRRIGATION : UNDERGROUND_WARMTH,
        durationMs,
      );
    }),
  );
  // Settle authority: setPhaseImmediate (force-solve / checkpoint seed)
  // emits only the settled pair — no transition event — so this snap is
  // load-bearing there; after a normal lerp it just pins exact finals.
  base.addUnsub(
    gameEvents.on(GameEvents.CONSERVATORY_PHASE_CHANGED, ({ phase }) => {
      postTween.settle(
        phase === "irrigation" ? CONSERVATORY_IRRIGATION : UNDERGROUND_WARMTH,
      );
    }),
  );

  // Opaque physical surfaces precede optional scenery acquisition. A failed
  // or incomplete GLB leaves the actual floor and all seven landings visible.
  const surfaces = createConservatorySurfaces(scene, physicsSystem);
  for (const surface of [surfaces.ground, ...surfaces.platforms]) {
    base.addDisposeCallback(() => surface.dispose());
  }
  const scenery = await loadConservatoryScenery(scene, shadowGen, surfaces);
  if (scenery) base.addDisposeCallback(() => scenery.dispose());
  const heroProps = await loadConservatoryRegulator(scene, shadowGen);
  for (const prop of heroProps) base.addDisposeCallback(() => prop.dispose());

  // Curated original flora/cakes supplement the constructed botanical beds.
  // The old dome panels and parallax layers are retired by this replacement.
  const VINE_PLACEMENTS: ScatterPlacement[] = [
    { x:2,y:6.5,z:-5.5,yaw:.2,scale:1.1 },
    { x:50,y:6.5,z:-5.3,yaw:-.15,scale:1 },
    { x:98,y:7,z:-5.5,yaw:.05,scale:1.2 },
    { x:146,y:6.5,z:-5.3,yaw:.35,scale:1.05 },
  ];
  const CAKE_PLACEMENTS: ScatterPlacement[] = [
    { x:14,y:.4,z:-2.2,yaw:.4,scale:.9 },
    { x:30,y:.4,z:-2.3,yaw:-.2,scale:1 },
    { x:78,y:.4,z:-2.5,yaw:.8,scale:.85 },
  ];
  const FERN_PLACEMENTS: ScatterPlacement[] = [
    { x:18,y:.3,z:-5,yaw:.5,scale:.9 },
    { x:72,y:.3,z:-5,yaw:.6,scale:1.1 },
    { x:129,y:.3,z:-3.3,yaw:.7,scale:1.3 },
    { x:137,y:.3,z:-3.3,yaw:-.4,scale:1.25 },
  ];
  const families = [
    { path: "/assets/models/environment/l2_iron_wood_vine.glb", tag: "l2_vine", placements: VINE_PLACEMENTS },
    { path: "/assets/models/environment/l2_petrified_layer_cake.glb", tag: "l2_cake", placements: CAKE_PLACEMENTS },
    { path: "/assets/models/environment/l2_bioluminescent_fern.glb", tag: "l2_fern", placements: FERN_PLACEMENTS },
  ];
  for (const family of families) {
    const scatter = await scatterInstances(scene, family.path, family.placements,
      { shadowGenerator: shadowGen, tag: family.tag });
    base.addDisposeCallback(() => scatter.dispose());
  }

  // Bounded rear atmosphere follows canonical phase events and the same
  // fixed clock as the light/grade. It owns phase-dependent emit rates,
  // including camera culling; CameraSystem must not restore stale rates.
  const floorMaterials = scene.materials.filter((material): material is PBRMaterial =>
    material instanceof PBRMaterial && ["Conservatory_fired_brick", "conservatoryFallbackFloorMat"].includes(material.name));
  const atmosphere = new ConservatoryAtmosphere(scene, floorMaterials, conservatoryCycle.canonicalPhase);
  base.addDisposeCallback(() => atmosphere.dispose());

  // --- Shadow zones (ShadowSlip + ConservatoryCycle) ---
  const { shadowZoneInfos, shadowZoneMeshes } = createShadowZones(scene);
  conservatoryCycle.registerShadowZones(shadowZoneMeshes);
  shadowSlip.registerShadowZones(shadowZoneInfos);

  // Register the loaded skins, not only capsules whose children may arrive
  // later. Keep the moving shadow volume around the actual shipping view.
  await Promise.all([minna.modelReady, basil.modelReady]);
  shadowGen.addShadowCaster(minna.mesh);
  shadowGen.addShadowCaster(basil.mesh);
  const key = visualHandles.keyLight;
  key.shadowMinZ = .1;
  key.shadowMaxZ = 80;
  const shadowDirection = key.direction.normalizeToNew();
  const updateShadowView = (): void => {
    const camera = cameraSystem.camera;
    const target = camera.getTarget();
    const snap = (value: number): number => Math.round(value * 64) / 64;
    key.position.set(snap(camera.position.x - shadowDirection.x * 30),
      snap(target.y - shadowDirection.y * 30), -shadowDirection.z * 30);
    const viewWidth = 2 * Math.abs(camera.position.z) * Math.tan(camera.fov / 2)
      * scene.getEngine().getAspectRatio(camera);
    key.shadowFrustumSize = Math.max(32, viewWidth + 4);
  };
  updateShadowView();
  base.addDisposeCallback(base.registerSafeBeforeRender("conservatoryShadowView", updateShadowView));

  // --- Co-op abilities ---
  // Resonant Drop is part of the L2 arrival kit (Elder Kitty's L1 reward).
  // Chronos Bubble is registered up front so the activation path exists,
  // but it is THIS level's Elder reward: it stays locked until Elder Gus
  // grants it at the apex (or a prior completed run already unlocked it
  // in the save — replays keep the earned power).
  const resonantDrop = new ResonantDrop(scene, basil.mesh, minna.mesh, {
    ringPlane: "ground", ringHeightOffset: -0.67, ringThicknessScale: 0.15,
    ringOpacity: 0.22, softParticles: true, particleDensityScale: 0.2,
  });
  const chronosBubble = new ChronosBubble(scene, basil.mesh, minna.mesh);
  basilAbilities.addAbility(resonantDrop);
  basilAbilities.addAbility(chronosBubble);

  let chronosBubbleUnlocked = SaveSystem.getInstance().isCoopAbilityUnlocked(
    "chronosBubble",
  );
  base.addUnsub(
    gameEvents.on(GameEvents.COOP_ABILITY_UNLOCKED, ({ abilityId }) => {
      if (abilityId === "chronosBubble") chronosBubbleUnlocked = true;
    }),
  );

  // ─── Puzzle Modules ──────────────────────────────────────────────
  //
  // Section layout (world X positions):
  //   Section 1:  0-40   (Puzzle 1 - Rusted Pipe)
  //   Section 2: 40-70   (Puzzle 2 - Baking)
  //   Section 3: 70-100  (Puzzle 3 - Day/Night Lever)
  //   Section 4: 100-150 (Apex - Stealth Escort + Elder)

  const puzzles: PuzzleModule[] = [
    new MasterValvePuzzle(conservatoryCycle),
    new NectarEscortPuzzle(chronosBubble, conservatoryCycle),
    new BoilerBoundSundewPuzzle(conservatoryCycle, chronosBubble),
  ];

  // Level-scoped shared puzzle state. The apex Sundew gates on
  // `nectarEscort.solved`; the escort reads `conservatoryCycle.phase`
  // directly rather than going through levelState.
  const levelState = new Map<string, PuzzleState>();

  // AuraSight interactive collection — puzzles contribute their meshes
  // here via `ctx.registerInteractive`, then we hand the full list to the
  // ability once every puzzle finishes setup.
  const allInteractives: InteractiveInfo[] = [];

  const puzzleCtx: PuzzleContext = {
    scene,
    physicsSystem,
    glowLayer,
    shadowGenerator: shadowGen,
    minna,
    basil,
    minnaAbilities,
    basilAbilities,
    auraSight,
    shadowSlip,
    calmingResonance,
    turnTableTouch,
    pneumaticAnchor,
    audioManager,
    cutsceneSystem,
    gameEvents,
    levelState,
    registerInteractive: (info) => {
      allInteractives.push(info);
    },
    addUnsub: (unsub) => base.addUnsub(unsub),
  };

  // Setup all puzzles. Sequential so async setup work runs in order.
  for (const puzzle of puzzles) {
    await puzzle.setup(puzzleCtx);
    levelState.set(puzzle.id, puzzle.getState());
  }

  // Expose loaded puzzle IDs for runtime parity testing against the
  // level's MechanicContract (see tests/integration/puzzle-id-parity.spec.ts).
  scene.metadata = {
    ...(scene.metadata ?? {}),
    loadedPuzzleIds: puzzles.map((p) => p.id),
  };

  // Wire Aura Sight with every contributed interactive.
  auraSight.registerInteractives(allInteractives);
  if (allInteractives.length >= 3) {
    // Connection hints: the first three interactives registered are the
    // Master Mist Regulator's two handles and the petrified vine choking
    // them, so Aura Sight draws the choke into the regulator it seizes.
    auraSight.registerConnections([
      [allInteractives[0], allInteractives[1]],
      [allInteractives[1], allInteractives[2]],
    ]);
  }

  // ─── Composite puzzle handle for QA tooling parity ───────────────
  const compositePuzzle = {
    id: "overgrownConservatoryComposite",
    getState: () => ({
      puzzles: Object.fromEntries(puzzles.map((p) => [p.id, p.getState()])),
    }),
    getInternals: () => ({
      masterValve: (puzzles[0] as MasterValvePuzzle).getInternals(),
      nectarEscort: (puzzles[1] as NectarEscortPuzzle).getInternals(),
      sundewApex: (puzzles[2] as BoilerBoundSundewPuzzle).getInternals(),
    }),
  };
  const levelHandleMeta = scene.metadata?.level as
    | {
        setPuzzle?: (p: unknown) => void;
        registerPuzzles?: (puzzles: PuzzleModule[], levelState: Map<string, PuzzleState>) => void;
      }
    | undefined;
  levelHandleMeta?.setPuzzle?.(compositePuzzle);
  // Expose per-puzzle modules to the QA harness: the hybrid
  // `moveToTarget` / `traverse` / `activateNear` / `waitForEvent`
  // primitives read QAHints via `scene.metadata.level.getQAHints()`
  // which aggregates across this list. Also enables `forceSolvePuzzle`
  // + `__startAtPuzzle` to short-circuit earlier puzzles deterministically.
  levelHandleMeta?.registerPuzzles?.(puzzles, levelState);

  // ─── Elder Gus (hidden until encounter) ──────────────────────────

  const parlor = new ConservatoryParlorVisuals(scene, parlorPlacement.sceneX, shadowGen);
  base.addDisposeCallback(() => parlor.dispose());
  const elderGusPos = new Vector3(parlorPlacement.sceneX + parlorPlacement.gus.x,
    parlorPlacement.gus.y, parlorPlacement.gus.z);
  const elderGus = new ElderGus(scene, elderGusPos, {
    seatY: parlorPlacement.seatTopY, shadowGenerator: shadowGen,
    readingLight: parlor.readingLight, yaw: parlorPlacement.gus.yaw,
    lampOffset: new Vector3(parlorPlacement.lamp.x - parlorPlacement.gus.x,
      parlorPlacement.lamp.y - parlorPlacement.gus.y, parlorPlacement.lamp.z - parlorPlacement.gus.z),
  });
  elderGus.setRevealed(false);
  base.addDisposeCallback(() => elderGus.dispose());
  // Gus is a cutscene actor. Aura must not resurrect his hidden fallback
  // capsule alongside the loaded cat; the separate throw owns interaction.

  // ── Making Biscuits anchor (Gus's woven throw) ──
  // Physical chair/throw remain in the reading alcove. Aura reveals only
  // the stitched memory, reachable before the existing automatic handoff.
  const gusBiscuit = setupElderBiscuitAnchor({
    scene,
    elder: "gus",
    minna, inputManager,
    cutsceneSystem,
    position: new Vector3(parlorPlacement.sceneX + parlorPlacement.anchor.x,
      parlorPlacement.anchor.y, parlorPlacement.anchor.z),
    modelPath: "/assets/models/environment/conservatory_gus_memory.glb",
    interactiveRegistry: allInteractives,
  });
  base.addDisposeCallback(() => gusBiscuit.dispose());

  // ─── Per-puzzle guidance (objective line + anchored cue chip) ─────
  //
  // Each puzzle's getCue() names its current step (the Sundew's follows
  // its four phases); the driver pins the first unsolved puzzle's cue to
  // the thing to act on, with an "(N/3)" objective line. It also drives
  // the top puzzle counter (1/3, 2/3, 3/3). See LevelCueDriver.
  const cueDriver = new LevelCueDriver(
    [
      { puzzle: puzzles[0], objective: "Turn the Master Mist Regulator" },
      { puzzle: puzzles[1], objective: "Lead the opossums to their nests" },
      { puzzle: puzzles[2], objective: "Unwire the Boiler-Bound Sundew" },
    ],
    gameEvents,
  ).attach(scene);
  base.addDisposeCallback(() => cueDriver.dispose());

  // ─── Exit portal — sealed until the apex Elder cutscene completes ──
  //
  // L1 has a Portal entity past the apex; L2 had nothing, so a player
  // had no visible "destination" anywhere in the 160-unit level — the
  // level ended via cutscene callback with zero north-star feedback.
  // A sealed portal at x=158 (just past the Sundew apex) gives the
  // player a goal to walk toward throughout the level; the Elder Gus
  // cutscene opens it in its level-complete callback, matching L1.
  //
  // Grounded at y=1: the earlier placement at y=16 floated the portal
  // 16 units above the only walkable surface (the floor tops out at
  // y=0), permanently outside both the camera frame and the cats'
  // reach — pure decoration where nobody could ever see it.
  const exitPortalConservatory = new Portal(scene, {
    key: "conservatoryPortal",
    position: new Vector3(158, 1, 0),
    label: "Portal to Clockwork Canopy",
    floorY: 0, shadowGenerator: shadowGen, brassGlowScale: 0,
    modelPath: "/assets/models/environment/conservatory_parlor_door.glb",
  });
  allInteractives.push({
    mesh: exitPortalConservatory.rootMesh,
    hidden: false,
    state: "revealed",
  });
  base.addDisposeCallback(() => exitPortalConservatory.dispose());

  // ─── Level state (shell only) ────────────────────────────────────

  let elderEncounterDone = false;
  let levelComplete = false;
  let elderEncounterTimer: ReturnType<typeof setTimeout> | null = null;
  let encounterOwnerRetired = false;
  base.addDisposeCallback(() => {
    encounterOwnerRetired = true;
    if (elderEncounterTimer !== null) clearTimeout(elderEncounterTimer);
    elderEncounterTimer = null;
  });

  // ─── Apex solve triggers the Elder encounter ─────────────────────
  base.addUnsub(gameEvents.on(GameEvents.PUZZLE_SOLVED, (data) => {
    if (levelComplete || encounterOwnerRetired || elderEncounterDone || elderEncounterTimer !== null) return;
    if (data?.puzzleId !== "sundewApex") return;
    // Delay slightly so the Phase 3 dash-sync flourish animation can
    // play out before the Elder fade begins.
    elderEncounterTimer = setTimeout(() => {
      elderEncounterTimer = null;
      if (!encounterOwnerRetired && !scene.isDisposed) startElderEncounter();
    }, 1500);
  }));

  function startElderEncounter(): void {
    if (encounterOwnerRetired || scene.isDisposed || elderEncounterDone) return;
    elderEncounterDone = true;
    gameEvents.emit(GameEvents.HUD_HIDE_PROMPT);

    gameEvents.emit(GameEvents.ELDER_ENCOUNTER, {
      elder: "gus",
      level: "OvergrownConservatory",
    });

    const dialogueSteps: CutsceneStep[] = ELDER_GUS_ENCOUNTER.map((line): CutsceneStep => ({
      type: "dialogue",
      dialoguePlacement: "top",
      speaker: line.speaker,
      text: line.text,
      duration: line.duration,
    }));

    const steps: CutsceneStep[] = [
      // Canon reveal: per book p. 19, "the feral greenhouse melts away
      // into a quiet, plush parlor. At its center sits Gus." The
      // `reveal` step plays the parlor-dissolve curtain — a Victorian
      // wingback-chair silhouette fades over a warm plum wash and
      // dissolves outward via chromatic aberration revealing Gus.
      { type: "fade", fadeDirection: "out", duration: 800 },
      { type: "callback", fn: () => {
        // Bring the existing cast into the parlor under the curtain. This
        // shares the shipping position owner, including velocity/drop reset.
        base.teleportCharacter("minna", 153.1, .735, 0);
        base.teleportCharacter("basil", 151.4, .735, 0);
        elderGus.setRevealed(true);
      }},
      { type: "reveal", preset: "parlor-dissolve", duration: 2500 },
      { type: "fade", fadeDirection: "in", duration: 800 },
      { type: "pan", target: { x: 153.8, y: 3.2 }, duration: 2000 },
      { type: "wait", duration: 500 },
      ...dialogueSteps,
      { type: "shake", duration: 500, intensity: 0.008 },
      { type: "callback", fn: () => {
        gameEvents.emit(GameEvents.COOP_ABILITY_UNLOCKED, {
          abilityId: "chronosBubble",
          abilityName: "Chronos Bubble",
        });
      }},
      { type: "wait", duration: 1000 },
      { type: "fade", fadeDirection: "out", duration: 1500 },
      { type: "callback", fn: () => {
        completeLevelSequence();
      }},
    ];

    cutsceneSystem.play(steps);
  }

  function completeLevelSequence(): void {
    levelComplete = true;
    gameEvents.emit(GameEvents.LEVEL_COMPLETE, { level: "OvergrownConservatory" });
    // Open the exit portal as the completion spectacle (same beat as L1's
    // Elder Kitty callback) — the transition itself is handled globally in
    // main.ts via the LEVEL_COMPLETE listener.
    exitPortalConservatory.open();
  }

  // ─── Dispose puzzles + Elder + environment on scene teardown ─────

  base.addDisposeCallback(() => {
    for (const puzzle of puzzles) {
      try { puzzle.dispose(); } catch (e) { console.warn("Puzzle dispose error:", e); }
    }
  });

  // ─── Wounds and Medicine: Grease Caches ─────────────────────────
  // Spec §4 places 2-3 caches per level for L1-L3. L2 damage sources
  // include steam vents and Fox hits, so a mid-map safety net matters.
  const greaseCachesL2 = [
    new GreaseCache(scene, {
      key: "greaseCache_L2_a",
      position: new Vector3(32, 1.2, 0),
      label: "Grease Cache",
    }),
    new GreaseCache(scene, {
      key: "greaseCache_L2_b",
      position: new Vector3(78, 1.2, 0),
      label: "Grease Cache",
    }),
    new GreaseCache(scene, {
      key: "greaseCache_L2_c",
      position: new Vector3(128, 1.2, 0),
      label: "Grease Cache",
    }),
  ];
  base.addDisposeCallback(() => {
    for (const cache of greaseCachesL2) {
      try { cache.dispose(); } catch (e) { console.warn("GreaseCache dispose error:", e); }
    }
  });

  // ─── Game loop ───────────────────────────────────────────────────

  base.registerGameLoop(
    (dt, sceneTime) => {
      const dtMs = dt * 1000;

      // Conservatory phase cycle update (system owned by the scene, driven every tick).
      conservatoryCycle.update(dtMs);
      // Q-034: grade tween rides the same clock as the light lerp. When
      // the cycle completes it synchronously emits CONSERVATORY_PHASE_CHANGED
      // inside update() above, the settle handler snaps exact finals, and
      // this tick becomes a no-op — no double-apply.
      postTween.update(dtMs);
      atmosphere.update(dtMs, cameraSystem.camera.getTarget().x, cameraSystem.camera.position.z);

      // Tick each puzzle and refresh its entry in levelState so later
      // puzzles (day/night, stealth escort) always see up-to-date
      // solved flags from their prerequisites.
      for (const puzzle of puzzles) {
        puzzle.update(dt, sceneTime);
        levelState.set(puzzle.id, puzzle.getState());
      }

      // Wounds/Medicine: tick grease caches so respawn timers advance.
      for (const cache of greaseCachesL2) {
        cache.tick(dt);
      }

      // Gus's local presentation stays seated at the measured cushion.
      if (elderGus.mesh.isEnabled()) {
        elderGus.update(sceneTime, dt);
      }

      // Making Biscuits: tick the minigame + proximity prompt for Gus's throw.
      gusBiscuit.update(dt);

      // Co-op ability updates. AbilityHolder.update() already ticks cooldowns
      // and calls update() on anything active, but ResonantDrop/ChronosBubble
      // were added directly via addAbility above so they're handled by
      // basilAbilities.update() in the BaseLevel game loop.
    },
    {
      isLevelComplete: () => levelComplete,
      onAbilityKey: (key, pressed, released) => {
        // Biscuits (B / A / D) take priority when a session is active.
        if (gusBiscuit.onAbilityKey(key, pressed, released)) {
          return true;
        }

        // Co-op combos live on the scene shell — both Resonant Drop (Level 1
        // unlock) and Chronos Bubble (this level's unlock) need a trigger.
        if (key === "f+m" && pressed) {
          tryCoopAbility(resonantDrop, "resonantDrop");
          return true;
        }

        if (key === "g+n" && pressed) {
          tryCoopAbility(chronosBubble, "chronosBubble", {
            unlocked: chronosBubbleUnlocked,
          });
          return true;
        }

        // Wounds/Medicine: let Basil spend a nearby Grease Cache via
        // the interact key. We check before puzzles so the cache
        // preempts overlapping puzzle interactions (consume only
        // succeeds if Basil is actually in radius AND the harness
        // needs healing, so puzzle-adjacent caches don't steal input).
        if (key === "," && pressed) {
          for (const cache of greaseCachesL2) {
            if (cache.isReady && cache.isInInteractRange(basil.mesh.position)) {
              if (cache.consume(base.harnessWounds, basil.mesh.position)) {
                return true;
              }
            }
          }
        }

        // Give each puzzle a chance to handle the key, latest first; the
        // first to return `true` consumes the input. Regulator cranks stay
        // live after their puzzle is solved: when the Master Valve's old
        // mid-escort cranks sat at x=129/131, inside Turn-Table reach of the
        // Sundew maw, array order let them grab Basil's `1` during the apex
        // fight (tools/playtest l2-route, 2026-09-27). The escort's mist
        // valve now sits at x=114.5, out of the maw's reach, but latest-first
        // still keeps the apex first in line. The Sundew only claims `1`
        // when one of its own valves/nodes is in reach.
        for (const puzzle of [...puzzles].reverse()) {
          if (puzzle.onAbilityKey && puzzle.onAbilityKey(key, pressed, released)) {
            return true;
          }
        }
        return false;
      },
    },
  );

  // ─── Start ───────────────────────────────────────────────────────

  setTimeout(() => audioManager.startAmbient(), 500);
  gameEvents.emit(GameEvents.LEVEL_STARTED, "OvergrownConservatory");

  // Opening cutscene with control hints.
  const introLines = LEVEL_INTRO["OvergrownConservatory"] ?? [];
  const introSteps: CutsceneStep[] = [
    { type: "fade", fadeDirection: "in", duration: 1200, color: { r: 0, g: 0, b: 0 } },
    { type: "wait", duration: 300 },
    { type: "pan", target: { x: 14, y: 7.5 }, duration: 900 },
    { type: "pan", target: { x: 42, y: 15.5 }, duration: 1200 },
    { type: "pan", target: { x: 8, y: 3.2 }, duration: 900 },
    ...introLines.map((line): CutsceneStep => ({
      type: "dialogue",
      dialoguePlacement: "top",
      speaker: line.speaker,
      text: line.text,
      duration: line.duration,
    })),
    { type: "dialogue", dialoguePlacement: "top", speaker: "", text: controlHintForMode(inputManager.soloMode), duration: 3500 },
    // The player arrives carrying Resonant Drop from Elder Kitty, but L2
    // never taught the chord anywhere — prompts only listed Q/E/F/1/2.
    { type: "dialogue", dialoguePlacement: "top", speaker: "", text: "Elder Kitty's gift travels with you — hold F + M together for Resonant Drop.", duration: 3500 },
    { type: "wait", duration: 300 },
  ];
  // `playDeferred` (not raw setTimeout) so that `__startAtPuzzle` can cancel
  // this pending intro before the timer fires — see
  // docs/qa/analysis/startatpuzzle-audit-2026-04-18.md.
  cutsceneSystem.playDeferred(introSteps, 600);

  return {
    physicsSystem,
    cameraSystem,
    inputManager,
    minna,
    basil,
    minnaAbilities,
    basilAbilities,
    audioManager,
    conservatoryCycle,
    cutsceneSystem,
  };
}

// ─── Helper: Shadow Zones ───────────────────────────────────────────

function createShadowZones(scene: Scene): {
  shadowZoneInfos: ShadowZoneInfo[];
  shadowZoneMeshes: Mesh[];
} {
  const shadowZoneDefs = [
    { position: new Vector3(10, 1, 0), width: 5, height: 1.5 },
    { position: new Vector3(45, 1, 0), width: 6, height: 1.5 },
    { position: new Vector3(65, 1, 0), width: 4, height: 1.2 },
    { position: new Vector3(105, 1, 0), width: 6, height: 2.0 },
    { position: new Vector3(125, 1, 0), width: 5, height: 1.8 },
    { position: new Vector3(145, 1, 0), width: 7, height: 2.0 },
  ];

  const shadowZoneMeshes: Mesh[] = [];
  const shadowZoneInfos: ShadowZoneInfo[] = [];

  for (let i = 0; i < shadowZoneDefs.length; i++) {
    const def = shadowZoneDefs[i];

    // Invisible trigger volume
    const trigger = MeshBuilder.CreateBox(
      `shadowZone_${i}`,
      { width: def.width, height: def.height, depth: 1.5 },
      scene,
    );
    trigger.position = def.position;
    trigger.isVisible = false;
    trigger.isPickable = false;

    // Atmospheric dark visual
    const visual = MeshBuilder.CreateBox(
      `shadowZoneVisual_${i}`,
      { width: def.width, height: def.height, depth: 1.5 },
      scene,
    );
    visual.position = def.position;
    visual.isVisible = WORLD_DEBUG.SHOW_SHADOW_ZONE_VISUALS;
    visual.isPickable = false;
    const darkMat = new StandardMaterial(`shadowZoneMat_${i}`, scene);
    darkMat.diffuseColor = new Color3(0.02, 0.01, 0.04);
    darkMat.emissiveColor = new Color3(0.01, 0.005, 0.02);
    darkMat.alpha = 0.45;
    visual.material = darkMat;

    shadowZoneMeshes.push(trigger);
    shadowZoneInfos.push({ mesh: trigger });
  }

  return { shadowZoneInfos, shadowZoneMeshes };
}
